import React, { useEffect, useMemo } from "react";
import classes from "./index.module.scss";
import { Button, Icon, Modal } from "antd";
import { getSignInfo, signInEnd, SignInInfo } from "service/signIn";
import ResultMessage from "components/CourseComponents/ResultMessage";
import { errorMessage } from "service/service";
import useRequestFn from "utils/useRequestFn";
import SettingModal from "components/CourseComponents/SignIn/SettingModal";
import useModalHandler from "utils/useModalHandler";
import moment from "moment";
import { useCurrentTimeSync } from "utils/useCurrentDate";
import usePromiseCallback from "utils/usePromiseCallback";
import { useInterval } from "react-use";

interface SignInCountDownProps {
    weekTimeId: number;
    signInId: number;
    onStatusChange: () => void;
    onCountDown: () => void;
    signInDetail: SignInInfo;
}

export default function SignInCountDown({
    weekTimeId,
    signInId,
    signInDetail,
    onStatusChange,
    onCountDown
}: SignInCountDownProps) {
    const model = useModalHandler();
    const [callSignInEnd, endState] = useRequestFn(signInEnd);

    const serverTime = useCurrentTimeSync();

    useInterval(onCountDown, signInDetail.refreshTime * 1000);

    const time = useMemo(() => {
        const millisecond = Math.max(
            serverTime.diff(moment(signInDetail.startTime)),
            0
        );
        const duration = moment.duration({ millisecond });
        const sec = duration.seconds();
        const totalSec = Math.round(Math.max(duration.asSeconds(), 0));
        const label =
            totalSec > 24 * 3600
                ? "超过一天"
                : `${Math.floor(duration.asMinutes())}:${
                      sec < 10 ? `0${sec}` : sec
                  }`;
        return { label, totalSec };
    }, [serverTime, signInDetail]);

    // 自动终止活动
    useEffect(() => {
        if (!signInDetail.validityTime) return;
        // 使用等于判断是防止多次重复提交
        if (time.totalSec >= signInDetail.validityTime * 60) onTimeout();
    }, [time.totalSec, signInDetail.validityTime]);

    async function onTimeout() {
        if (!signInDetail?.validityTime) return;

        try {
            const newSignInfo = await getSignInfo(signInId);

            if (signInDetail.validityTime === newSignInfo.validityTime)
                await onSignInEndClick(false);
            else onStatusChange();
        } catch (e) {
            Modal.error({ content: errorMessage(e) });
        }
    }

    // 结束签到
    const onSignInEndClick = usePromiseCallback(async function (
        showError: boolean = true
    ) {
        try {
            await callSignInEnd(signInId);
        } catch (e) {
            // 自动结束如果报错不进行提示
            if (showError)
                ResultMessage.error(errorMessage(e), { duration: 3000 });
        } finally {
            onStatusChange();
        }
    });

    return (
        <div className={classes.root}>
            <div className={classes.time}>{time.label}</div>
            <button className={classes.settingBtn} onClick={model.open}>
                <Icon type="setting" />
            </button>
            <Button
                type="primary"
                ghost
                className={classes.button}
                onClick={onSignInEndClick}
                disabled={endState.loading}>
                <Icon type="poweroff" /> 结束签到
            </Button>

            <SettingModal
                visible={model.visible}
                onClose={model.close}
                weekTimeId={weekTimeId}
                onSuccess={onStatusChange}
                defaultData={signInDetail}
            />
        </div>
    );
}
